import moment from 'moment';
import { styled } from 'styled-components';
import * as React from 'react';
import * as qs from 'querystring';

import { Redirect, RedirectProps, RouteComponentProps } from 'react-router-dom';
import { connect } from 'react-redux';
import { withTranslation, WithTranslation } from 'react-i18next';

import * as api from '../../api';
import * as logger from '../../logger';
import * as rootReducer from '../../reducers/root';
import * as store from '../../reducers/store';
import { applyTheme } from '../../utils';
import { getNextIndex } from '../../utils/navigation';
import { PatientRouteParams } from '../router';

import { getMaybeAutocompletedDateOfBirth } from './utils';
import { Button } from '../../components/Button';
import { DatePicker } from '../../components/DatePicker';
import TwoFactor from './TwoFactor';
import Disclaimer from './Disclaimer';
import Footer from './Footer';
import LanguageSelection from './LanguageSelection';
import Loader from '../../components/Loader';
import NoCookies from './NoCookies';
import NavBar, { View } from '../../components/NavBar';
import SignInSupport from './SignInSupport';
import ZipSelection from './ZipSelection';

import { parseValue } from 'common/dist/utils/types';

import { getFormStartPath } from '../../utils/form';
import { Forms } from '../../reducers/store';
import { onKeyPress } from '../../utils/keypress';

import styles from './index.module.css';
import SsoButton from '../../components/SsoButton';

const { Primitive } = api.PrevisitFormTemplateFieldConfig;
const { AuthErrorType } = api.Previsit;

///////////////////////////////////////////////////
//////INTERFACES

interface StateProps {
  preferredLanguage: store.Language;
}

interface Props
  extends DispatchProps,
    WithTranslation,
    RouteComponentProps<
      PatientRouteParams,
      Exclude<RouteComponentProps['staticContext'], undefined>,
      { from: RedirectProps['to'] }
    >,
    StateProps {
  overrideRedirectUrl?: string;
}

interface DispatchProps {
  setLoggedInPatient: (loggedInPatient: store.Patient) => void;
  setFormSets: (
    formSets: api.PrevisitFormSet.PrevisitFromSetWithPatientData[],
  ) => void;
  setFeedback: (feedback: store.PatientFeedback | null) => void;
  setTheme: (theme: api.PracticeTheme.ThemeResponse) => void;
  setPreferredLanguage: (language: store.Language) => void;
}
interface State {
  lastName: string;
  dateOfBirth?: Date;
  email: string | null;
  phoneNumber: string | null;
  redirect: boolean;
  showZipPage: boolean;
  challengeZips?: string[];
  zipError?: string;
  maxAttemptsReached?: boolean;
  readyForLogin: boolean;
  isLoading: boolean;
  isSubmitting: boolean;
  isZipRequired: boolean;
  showTwoFactorModal: boolean;
  signInSupportPage?: api.PracticePreference.LocalizedPreference;
  supportedLanguages: api.Language.Language[];
  multi?: boolean;
  code?: string;
  forms?: Forms;
  zip?: string;
  //This field will be set to true if user jumps from an appointment confirmation page
  // In which case we will want to display a confirmation modal
  appointmentConfirmationModalOpen: boolean;
}

///////////////////////////////////////////////////
////// COMPONENT

export class LoginComponent extends React.Component<Props, State> {
  private monthInput = React.createRef<HTMLInputElement>();
  private dayInput = React.createRef<HTMLInputElement>();
  private yearInput = React.createRef<HTMLInputElement>();
  private lastNameInput = React.createRef<HTMLInputElement>();
  private languageModalWrapper = React.createRef<HTMLDivElement>();
  private languageModalButtons: React.RefObject<HTMLButtonElement>[] = [];

  state: State = {
    lastName: '',
    email: null,
    phoneNumber: null,
    redirect: false,
    showZipPage: false,
    readyForLogin: true,
    isLoading: true,
    isSubmitting: false,
    isZipRequired: false,
    showTwoFactorModal: false,
    supportedLanguages: [],
    multi: false,
    appointmentConfirmationModalOpen: false,
  };

  async componentDidMount(): Promise<void> {
    const { preferredLanguage, t } = this.props;
    const { authenticationCode } = this.props.match.params;
    const queryValues = qs.parse(this.props.location.search.slice(1));
    const source = queryValues && `${queryValues.source}`;
    this.props.setFeedback(null);
    if (preferredLanguage && preferredLanguage !== api.Language.Language.En) {
      this.props.setPreferredLanguage(preferredLanguage);
    }

    this.setState({
      ...this.state,
      multi: queryValues.future === 'true',
    });

    try {
      const [isAuthenticated] = await Promise.all([
        api.client.Previsit.isAuthenticated(),
        api.client.Previsit.createPrevisitLog({
          auditLog:
            api.Log.PrevisitAuditAction.PatientPrevisitNavigatedToSignInPage,
          previsitLog: api.Log.PrevisitAction.NavigatedToSignInPage,
          authenticationCode,
          source,
        }),
        this.getAndApplyLoginTheme(authenticationCode),
      ]);
      if (!isAuthenticated) {
        return;
      }
    } catch (err) {
      logger.error(
        err,
        `[Login.componentDidMount] ${t(
          'somethingWentWrongRefreshOrContactSupport',
        )}`,
      );
      this.setState({ readyForLogin: false });
      alert(t('somethingWentWrongRefreshOrContactSupport'));
      return;
    } finally {
      this.setState({ isLoading: false });
    }

    try {
      // Make sure they're logged in as the correct user
      await api.client.Previsit.fetchFormSets(authenticationCode);
    } catch (err) {
      // They're logged in as a different user, or their code changed
      return;
    }

    return this.setState({
      readyForLogin: true,
      redirect: true,
    });
  }

  async getAndApplyLoginTheme(authCode: string) {
    try {
      const theme = await api.client.Previsit.getThemeForLogin({
        domain: window.location.hostname,
        authCode,
      });
      this.props.setTheme(theme);
      const { supportedLanguages } = theme;
      this.setState({
        supportedLanguages: supportedLanguages ?? [],
        signInSupportPage: theme.signInSupportPage,
        isZipRequired: !!theme.previsitAdditionalLoginInputs?.includes(
          Primitive.PatientAddressMailing,
        ),
      });
      if (
        this.props.preferredLanguage !== api.Language.Language.En &&
        !supportedLanguages?.includes(this.props.preferredLanguage)
      ) {
        this.props.setPreferredLanguage(api.Language.Language.En);
      }
      if (this.state.supportedLanguages.length) {
        [
          api.Language.Language.En,
          'Close',
          ...this.state.supportedLanguages,
        ].forEach((_, i) => {
          if (!this.languageModalButtons[i]) {
            this.languageModalButtons[i] = React.createRef<HTMLButtonElement>();
          }
        });
      }
      applyTheme(theme);
    } catch (e) {
      return;
    }
  }

  setLastName = (event: React.FormEvent<HTMLInputElement>) => {
    const { value: lastName } = event.currentTarget;
    this.setState({ lastName });
  };

  setDateOfBirth = (value: Date) => {
    this.setState({
      dateOfBirth: value,
    });
  };

  setZip = (e: React.FormEvent<HTMLInputElement>) => {
    this.setState({ zip: e.currentTarget.value });
  };

  getLoginFields = (): {
    authenticationCode: string;
    lastName: string;
    dateOfBirth: Date;
    code?: string;
    zip?: string;
  } | null => {
    const { authenticationCode } = this.props.match.params;
    const { zip, isZipRequired, code } = this.state;
    const lastName = this.state.lastName || this.lastNameInput.current?.value;
    const dateOfBirth =
      this.state.dateOfBirth || this.getMaybeAutocompletedDateOfBirth();
    if (!moment(dateOfBirth).isValid()) {
      alert(
        this.props.t('Invalid date of birth format: {{dateOfBirth}}', {
          dateOfBirth: this.state.dateOfBirth,
        }),
      );
      return null;
    }
    if (!lastName || !dateOfBirth) {
      alert(`${this.props.t('lastNameAndDateOfBirthMustBeProvided')}`);
      return null;
    }
    const loginFields = { authenticationCode, lastName, dateOfBirth, code };
    if (!isZipRequired) return loginFields;
    const regex = /^\d{5}/g;
    if (!zip || !regex.test(zip)) {
      alert(`${this.props.t('informationNotMatchedEnterDOBLastNameZip')}`);
      return null;
    }
    return { ...loginFields, zip };
  };

  showZipLogin = async () => {
    const loginFields = this.getLoginFields();
    if (!loginFields) {
      this.setState({ isSubmitting: false });
      return;
    }
    try {
      const { zips } = await api.client.Previsit.getLoginChallenge(
        loginFields.authenticationCode,
        loginFields.lastName,
        moment(loginFields.dateOfBirth).format('MM/DD/YYYY'),
      );
      this.setState({ challengeZips: zips, showZipPage: true });
    } catch (err) {
      alert(this.props.t('errorLoggingIn'));
    }
    this.setState({ isSubmitting: false });
  };

  showTwoFactorModal = async () => {
    const loginFields = this.getLoginFields();
    if (!loginFields) return;
    const { email, phoneNumber } = await api.client.Previsit.getContacts({
      ...loginFields,
      dateOfBirth: moment(loginFields.dateOfBirth).format('MM/DD/YYYY'),
    });
    this.setState({ email, phoneNumber, showTwoFactorModal: true });
  };

  handleSubmit = async (evt: React.FormEvent<HTMLFormElement>) => {
    evt.preventDefault();
    evt.stopPropagation();
    this.setState({ isSubmitting: true });
    const { isZipRequired } = this.state;
    try {
      await this.login();
    } catch (err) {
      let errorMessage = 'errorLoggingIn';
      const loginError = parseValue<{ message: string }>(err.message);
      if (loginError?.message === AuthErrorType.RequiredCode) {
        return this.showTwoFactorModal();
      }
      if (loginError?.message === AuthErrorType.RequiredZip) {
        return this.showZipLogin();
      }
      if (loginError?.message === AuthErrorType.Expired) {
        errorMessage = 'linkExpiredCheckForNewerLink';
      } else if (loginError?.message === AuthErrorType.EncounterDeleted) {
        errorMessage = 'linkNoLongerValid';
      } else if (
        loginError?.message === AuthErrorType.EmptyFormSet ||
        loginError?.message === AuthErrorType.ClosedFormSet
      ) {
        errorMessage = 'thankYouForResponseNoFurtherAction';
      } else if (loginError?.message === AuthErrorType.Unauthenticated) {
        if (isZipRequired === true) {
          errorMessage = 'informationNotMatchedEnterDOBLastNameZip';
        } else {
          errorMessage = 'enterPatientDateOfBirthAndLastName';
        }
      } else if (loginError?.message === AuthErrorType.MaxAttempts) {
        errorMessage = 'maxLoginAttemptsReachedCallClinicForHelp';
      }
      alert(this.props.t(errorMessage));
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  getMaybeAutocompletedDateOfBirth = () => {
    const month = this.monthInput.current?.value;
    const day = this.dayInput.current?.value;
    const year = this.yearInput.current?.value;
    return getMaybeAutocompletedDateOfBirth({ month, day, year });
  };

  handlePatientLogin = async (patient: api.Patient.Patient) => {
    const { match, setFormSets, setLoggedInPatient, preferredLanguage } =
      this.props;
    const { authenticationCode } = match.params;

    // TODO: Temporary form fetch to avoid brief show of stale forms on login
    const { formSets } = await api.client.Previsit.fetchFormSets(
      authenticationCode,
      preferredLanguage,
      this.state.multi,
      true,
    );
    setFormSets(formSets);
    setLoggedInPatient(patient);
    this.setState({ redirect: true });
  };

  login = async () => {
    const { authenticationCode } = this.props.match.params;
    const loginFields = this.getLoginFields();
    if (!loginFields) return;
    const { patient } = await api.client.Previsit.login({
      ...loginFields,
      authenticationCode,
      dateOfBirth: moment(loginFields.dateOfBirth).format('MM/DD/YYYY'),
    });
    await this.handlePatientLogin(patient);
  };

  loginWithZipSelection = async (args: {
    lastName: string;
    dateOfBirth: Date;
    zip: string;
  }) => {
    const { authenticationCode } = this.props.match.params;
    const { lastName, dateOfBirth, zip } = args;
    try {
      const { patient } = await api.client.Previsit.login({
        authenticationCode,
        lastName,
        dateOfBirth: moment(dateOfBirth).format('MM/DD/YYYY'),
        zip,
      });
      if (patient) this.handlePatientLogin(patient);
    } catch (err) {
      const loginError = parseValue<{ message: string }>(err.message);
      if (loginError?.message === AuthErrorType.MaxAttempts) {
        this.setState({
          maxAttemptsReached: true,
          zipError: 'maxLoginAttemptsReachedCallClinicForHelp',
          challengeZips: [],
          showZipPage: true,
        });
        return;
      }
      if (loginError?.message === AuthErrorType.Unauthenticated) {
        this.setState({ zipError: 'incorrectZipCode' });
        return;
      }
      if (loginError?.message === AuthErrorType.Expired) {
        this.setState({ zipError: 'linkExpiredCheckForNewerLink' });
        return;
      }
      this.setState({ zipError: 'errorLoggingIn' });
    }
  };

  onCloseTwoFactorModal = () => {
    this.setState({ showTwoFactorModal: false, code: undefined });
  };

  setCode = (code?: string) => {
    this.setState({ code });
  };

  handleGenerateCode = async (
    args:
      | { email: string; phoneNumber?: undefined }
      | { email?: undefined; phoneNumber: string },
  ) => {
    const loginFields = this.getLoginFields();
    if (!loginFields) return;
    await api.client.Previsit.generateCode({
      ...args,
      ...loginFields,
      dateOfBirth: moment(loginFields.dateOfBirth).format('MM/DD/YYYY'),
    });
  };

  render() {
    const {
      location: { state: referrerState },
      overrideRedirectUrl,
    } = this.props;
    const { authenticationCode } = this.props.match.params;
    const { redirect, isLoading } = this.state;
    const { from: formLocation } = referrerState || {
      from: getFormStartPath({
        authenticationCode,
        multi: this.state.multi,
        allowReview: true,
      }),
    };

    if (isLoading) {
      return <Loader />;
    }
    if (redirect) {
      return <Redirect to={overrideRedirectUrl || formLocation} />;
    }
    if (this.state.showZipPage) {
      return this.renderZip();
    }
    return this.renderLogin();
  }

  renderSignInSupport = () => {
    const { signInSupportPage } = this.state;
    const { preferredLanguage } = this.props;
    if (!signInSupportPage) return;
    if (!preferredLanguage || !signInSupportPage[preferredLanguage]) {
      return (
        <SignInSupport
          content={signInSupportPage[api.Language.Language.En] || ''}
        />
      );
    }
    return (
      <SignInSupport content={signInSupportPage[preferredLanguage] || ''} />
    );
  };

  renderZip() {
    return (
      <ZipSelection
        zipError={this.state.zipError}
        maxAttempts={this.state.maxAttemptsReached}
        zipCodes={this.state.challengeZips || []}
        login={(zip) => {
          if (!zip) {
            this.setState({
              zipError: 'invalidZipSelected',
            });
            return;
          }
          const loginFields = this.getLoginFields();
          if (!loginFields) return;
          this.loginWithZipSelection({ ...loginFields, zip });
        }}
      />
    );
  }

  renderLogin() {
    const { t } = this.props;
    let selectedLanguageModalButtonIndex: number | undefined = undefined;
    const navigateLanguageButton = (step: number) => {
      selectedLanguageModalButtonIndex = getNextIndex(
        selectedLanguageModalButtonIndex,
        step,
        this.languageModalButtons.length,
      );
      if (selectedLanguageModalButtonIndex === undefined) return;
      this.languageModalButtons[
        selectedLanguageModalButtonIndex
      ].current?.focus();
    };
    return (
      <div
        className={styles.LoginWrapper}
        onKeyUp={(e) => {
          if (this.languageModalWrapper.current) {
            e.preventDefault();
            onKeyPress({
              e,
              onClose: () => this.languageModalWrapper.current?.click(),
              onNext: () => navigateLanguageButton(1),
              onPrevious: () => navigateLanguageButton(-1),
            });
          }
        }}
        onMouseUp={() => (selectedLanguageModalButtonIndex = undefined)}
      >
        <TwoFactor
          {...this.state}
          open={this.state.showTwoFactorModal}
          onClose={this.onCloseTwoFactorModal}
          setCode={this.setCode}
          handleGenerateCode={this.handleGenerateCode}
          login={this.login}
        />
        <NavBar view={View.Login} />
        <div className={styles.Login}>
          <NoCookies />
          <div className={styles.Instructions}>
            {t('For your security,')}
            <br />
            {t('sign in to continue.')}
          </div>
          <SsoButton
            onSsoSuccess={(patient) => this.handlePatientLogin(patient)}
            onSsoFailure={() => alert(this.props.t('errorLoggingIn'))}
          />
          <form onSubmit={this.handleSubmit}>
            <div className={styles.Inputs}>
              <DatePicker
                className={styles.SignInDatePicker}
                isLogin={true}
                onChange={this.setDateOfBirth}
                placeholder={t('patientDateOfBirth')}
                monthInput={this.monthInput}
                dayInput={this.dayInput}
                yearInput={this.yearInput}
              />
              <input
                id="patient-last-name"
                className={styles.Input}
                type="text"
                value={this.state.lastName}
                placeholder={t('patientLastName')}
                onChange={this.setLastName}
                ref={this.lastNameInput}
              />
              {this.state.isZipRequired && (
                <input
                  id="patient-zip"
                  className={styles.Input}
                  type="text"
                  value={this.state.zip || ''}
                  placeholder={t('zipCode')}
                  onChange={this.setZip}
                  maxLength={5}
                  inputMode="numeric"
                />
              )}
            </div>
            <ButtonSignIn
              id="sign-in-button"
              children={t('signIn')}
              type="submit"
              disabled={!this.state.readyForLogin || this.state.isSubmitting}
            />
          </form>
          {!this.state.showTwoFactorModal && (
            <>
              {this.renderSignInSupport()}
              <Disclaimer />
              <LanguageSelection
                selectedLanguage={
                  this.props.preferredLanguage || api.Language.Language.En
                }
                supportedLanguages={this.state.supportedLanguages}
                onLanguageSelected={(language) => {
                  this.props.setPreferredLanguage(language);
                }}
                languageModalWrapper={this.languageModalWrapper}
                languageModalButtons={this.languageModalButtons}
              />
            </>
          )}
        </div>

        {!this.state.showTwoFactorModal && <Footer />}
      </div>
    );
  }
}

const mapStateToProps = (state: store.Loaded): StateProps => ({
  preferredLanguage: state.preferredLanguage,
});

function mapDispatchToProps(dispatch: Function): DispatchProps {
  return {
    setLoggedInPatient: (patient: store.Patient) =>
      dispatch(rootReducer.setLoggedInPatient(patient)),
    setFormSets: (
      formSets: api.PrevisitFormSet.PrevisitFromSetWithPatientData[],
    ) => dispatch(rootReducer.setFormSets({ formSets })),
    setFeedback: (feedback: store.PatientFeedback | null) =>
      dispatch(rootReducer.setPatientFeedback(feedback)),
    setTheme: (theme: api.PracticeTheme.ThemeResponse) =>
      dispatch(rootReducer.setTheme(theme)),
    setPreferredLanguage: (language: store.Language) =>
      dispatch(rootReducer.setPreferredLanguage(language)),
  };
}
const ConnectedLogin = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withTranslation(['login'])(LoginComponent));
export default ConnectedLogin;

const ButtonSignIn = styled(Button)`
  margin-top: 1rem;
`;
